import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

const AuthContext = createContext(null);

const API_URL = getApiUrl();

// Helper to get auth headers for mobile fallback
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Try cookie-based auth first, with token header fallback for mobile
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
      setUser(response.data);
      // Update sessionStorage as backup
      try {
        sessionStorage.setItem('user_data', JSON.stringify(response.data));
      } catch (e) {}
    } catch (error) {
      console.log('[AuthContext] Auth failed, checking local storage');
      // If auth fails, check if we have stored user data
      try {
        const storedUser = sessionStorage.getItem('user_data');
        const storedToken = localStorage.getItem('session_token');
        
        if (storedUser && storedToken) {
          // Verify token is still valid
          try {
            const verifyResponse = await axios.get(`${API_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${storedToken}` }
            });
            setUser(verifyResponse.data);
            sessionStorage.setItem('user_data', JSON.stringify(verifyResponse.data));
          } catch (verifyError) {
            console.log('[AuthContext] Token verification failed');
            localStorage.removeItem('session_token');
            sessionStorage.removeItem('user_data');
            setUser(null);
          }
        } else {
          console.log('[AuthContext] No stored credentials');
          setUser(null);
        }
      } catch (e) {
        console.log('[AuthContext] Storage error:', e);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from legacy OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = async (credential) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/google`,
        { credential },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.status === 'pending') {
        alert('Your account is pending approval. Please contact an administrator.');
        return false;
      }

      // Store session token for mobile browsers
      if (response.data.session_token) {
        try { localStorage.setItem('session_token', response.data.session_token); } catch (e) {}
      }
      // Store CSRF token as backup in case the cookie is blocked by the browser
      // (e.g. Safari cross-site cookie restrictions). The axios interceptor
      // prefers cookie, falls back to localStorage.
      if (response.data.csrf_token) {
        try { localStorage.setItem('csrf_token', response.data.csrf_token); } catch (e) {}
      }

      const { session_token, csrf_token, ...userData } = response.data;
      setUser(userData);
      try { sessionStorage.setItem('user_data', JSON.stringify(userData)); } catch (e) {}
      return true;
    } catch (error) {
      console.error('[AuthContext] Google login failed:', error);
      const errorMsg = error.response?.data?.detail || 'Authentication failed. Please try again.';
      alert(errorMsg);
      return false;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { 
        withCredentials: true,
        headers: getAuthHeaders()
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      try {
        localStorage.removeItem('session_token');
        localStorage.removeItem('csrf_token');
        sessionStorage.removeItem('user_data');
      } catch (e) {}
      window.location.href = '/';
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginWithGoogle, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
